"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

const lines = [
  {
    name: "Red Line",
    descriptor: "Grounded",
    color: "#8B3A3A",
    bg: "rgba(139,58,58,0.04)",
    border: "rgba(139,58,58,0.2)",
    hoverBorder: "#8B3A3A",
    description: "For evenings and wind-down moments. A deeper, more settled experience for those who know what they're after.",
    emoji: "🔴",
  },
  {
    name: "White Line",
    descriptor: "Focused",
    color: "#4A5C6B",
    bg: "rgba(74,92,107,0.04)",
    border: "rgba(74,92,107,0.2)",
    hoverBorder: "#4A5C6B",
    description: "Clean and sharp. For mornings, work sessions, or any time clarity matters more than anything else.",
    emoji: "⚪",
  },
  {
    name: "Green Line",
    descriptor: "Balanced",
    color: "#3F5A45",
    bg: "rgba(63,90,69,0.04)",
    border: "rgba(63,90,69,0.2)",
    hoverBorder: "#3F5A45",
    description: "The all-day companion. Smooth, even, consistent — the line most users come back to first.",
    emoji: "🟢",
  },
];

export default function PickYourLineSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15 }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-28"
      style={{ backgroundColor: "#F6F5F2" }}
      id="lines"
    >
      <div className="max-w-container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={(el) => { cardRefs.current[0] = el; }}
          className="reveal mb-16 max-w-xl"
        >
          <span className="section-label mb-4 block">The Collection</span>
          <h2
            className="font-serif font-bold text-charcoal"
            style={{
              fontFamily: "Fraunces, Georgia, serif",
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              letterSpacing: "-0.025em",
              lineHeight: "1.1",
              color: "#1F1F1F",
            }}
          >
            Pick Your Line.
            <br />
            <em style={{ color: "#3F5A45" }}>One Kilo. Done Right.</em>
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {lines.map((line, i) => (
            <div
              key={line.name}
              ref={(el) => { cardRefs.current[i + 1] = el; }}
              className={`reveal stagger-${i + 1}`}
            >
              <div
                className="group relative p-8 h-full flex flex-col cursor-pointer transition-all duration-350"
                style={{
                  backgroundColor: "white",
                  border: `1.5px solid ${line.border}`,
                  transition: "all 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = line.hoverBorder;
                  el.style.transform = "translateY(-6px)";
                  el.style.boxShadow = "0 20px 48px rgba(31,31,31,0.08)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = line.border;
                  el.style.transform = "translateY(0)";
                  el.style.boxShadow = "none";
                }}
              >
                {/* Color accent bar */}
                <div
                  className="w-12 h-1 mb-8 transition-all duration-300"
                  style={{
                    backgroundColor: line.color,
                  }}
                />

                {/* Line name */}
                <div className="mb-2">
                  <span
                    className="text-xs font-semibold uppercase tracking-widest"
                    style={{ color: line.color, fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {line.name}
                  </span>
                </div>

                {/* Descriptor */}
                <h3
                  className="font-serif font-bold mb-4"
                  style={{
                    fontFamily: "Fraunces, Georgia, serif",
                    fontSize: "2.25rem",
                    letterSpacing: "-0.025em",
                    color: "#1F1F1F",
                    lineHeight: "1.1",
                  }}
                >
                  {line.descriptor}
                </h3>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed mb-8 flex-1"
                  style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {line.description}
                </p>

                {/* CTA */}
                <Link
                  href={`/shop?line=${line.name.split(" ")[0].toLowerCase()}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-all duration-200"
                  style={{
                    color: line.color,
                    fontFamily: "Inter, system-ui, sans-serif",
                    borderBottom: `1.5px solid ${line.color}`,
                    paddingBottom: "2px",
                  }}
                >
                  Shop 1 Kilo
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div
          ref={(el) => { cardRefs.current[4] = el; }}
          className="reveal mt-10 text-center"
        >
          <p
            className="text-sm"
            style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
          >
            All lines: 1 kilo · Single-origin sourced · Third-party lab tested · Free sample included
          </p>
        </div>
      </div>
    </section>
  );
}